import { Info, Sparkles } from "lucide-react";
import type { FC } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslate } from "@/utils/i18n";
import { cacheService, validationService } from "../services";
import { useEditorContext } from "../state";
import InsertMenu from "../Toolbar/InsertMenu";
// ❌ REMOVED: VisibilitySelector import (v0.66.0 - all memos are private)
import type { EditorToolbarProps } from "../types";

export const EditorToolbar: FC<EditorToolbarProps> = ({ onSave, onCancel, memoName, editorRef, cacheKey, aiEnabled, onAiToggle }) => {
  const { state, actions, dispatch } = useEditorContext();
  const { valid } = validationService.canSave(state);
  const { currentUser } = useAuth();
  const t = useTranslate();

  const isSaving = state.ui.isLoading.saving;

  const handleLocationChange = (location: typeof state.metadata.location) => {
    dispatch(actions.setMetadata({ location }));
  };

  const handleToggleFocusMode = () => {
    dispatch(actions.toggleFocusMode());
  };

  // ❌ REMOVED: handleVisibilityChange (v0.66.0 - visibility cannot be changed)

  const handleInsertSelfContract = () => {
    editorRef?.current?.insertSelfContract();
  };

  const handleInsertDecision = () => {
    editorRef?.current?.insertDecision?.();
  };

  const handleInsertChecklist = () => {
    editorRef?.current?.insertChecklist?.();
  };

  const handleCancel = () => {
    // Clear localStorage cache when canceling to prevent stale content/height on next edit
    if (cacheKey && currentUser) {
      cacheService.clear(cacheService.key(currentUser.name || "", cacheKey));
    }
    // Call parent's onCancel handler
    onCancel?.();
  };

  return (
    <div className="w-full flex flex-row justify-between items-center mb-2">
      <div className="flex flex-row justify-start items-center">
        <InsertMenu
          editorRef={editorRef}
          isUploading={state.ui.isLoading.uploading}
          location={state.metadata.location}
          onLocationChange={handleLocationChange}
          onToggleFocusMode={handleToggleFocusMode}
          onInsertSelfContract={handleInsertSelfContract}
          onInsertDecision={handleInsertDecision}
          onInsertChecklist={handleInsertChecklist}
          memoName={memoName}
        />
      </div>

      <div className="flex flex-row justify-end items-center gap-2">
        {/* AI Toggle - Only show when AI features are supported */}
        {onAiToggle !== undefined && (
          <Popover>
            <PopoverTrigger asChild>
              <div className="flex items-center gap-2 px-2 py-1 rounded-md border border-border bg-background cursor-help hover:bg-muted/50 transition-colors">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-xs text-muted-foreground">{t("editor.toolbar.ai-analysis")}</span>
                <Switch checked={aiEnabled} onCheckedChange={onAiToggle} disabled={isSaving} />
                <Info className="h-3 w-3 text-muted-foreground" />
              </div>
            </PopoverTrigger>
            <PopoverContent sideOffset={8} align="end" className="w-64 text-xs">
              <p className="text-muted-foreground">{t("editor.toolbar.ai-analysis-tooltip")}</p>
              <p className="text-muted-foreground mt-2 text-[10px]">{t("editor.toolbar.ai-analysis-disabled-tooltip")}</p>
            </PopoverContent>
          </Popover>
        )}

        {/* ❌ REMOVED: VisibilitySelector (v0.66.0 - all memos are private) */}

        {onCancel && state.content.trim().length > 0 && (
          <Button variant="ghost" onClick={handleCancel} disabled={isSaving}>
            {t("editor.toolbar.cancel")}
          </Button>
        )}

        <Button onClick={onSave} disabled={!valid || isSaving}>
          {isSaving ? t("editor.toolbar.saving") : t("editor.toolbar.save")}
        </Button>
      </div>
    </div>
  );
};
